import { __decorate } from "tslib";
import Vue from 'vue';
import dayjs from 'dayjs';
import { Component, Watch } from 'vue-property-decorator';
import { clone, omit } from 'ramda';
import { EventsWebApi } from '@/api/events/EventsApi';
import { getFirstDayOfMonth, getLastDayOfMonth, formatDateForTitle, equalByDay, getDateAsIsoString, formatDateDayMonthYearByLocale, needMakeRequest, BASE_BACK_TIME_FORMAT, } from '@/helpers/dates';
let ReservedSlotsMixin = class ReservedSlotsMixin extends Vue {
    loading = false;
    localDate = new Date();
    changedCalendarDate = new Date();
    lastEventDate = new Date();
    reservedSlots = [];
    availabilitySlots = [];
    forceTempHidePopup = false;
    showSubPopup = false;
    instanceData = null;
    showReservedSlotPopup = false;
    meta = null;
    events = [];
    currentSlot = null;
    currentAvailabilitySlot = null;
    selectedSlot = null;
    forceShowAddReservedSlot = false;
    isRequestSending = false;
    forceEmptyInstance = false;
    isSkillSelectStep = false;
    baseFormat = `E, dd MMM yyyy HH:mm:ss`;
    get date() {
        if (this.localDate) {
            return formatDateForTitle(this.localDate);
        }
        return null;
    }
    get currentSlots() {
        if (this.localDate) {
            return this.reservedSlots.filter(item => this.equalByDay(item.date));
        }
        return null;
    }
    get currentInstancesData() {
        if (this.localDate) {
            return this.events.filter(event => {
                const condition = this.selectedSlot ? event.id === this.selectedSlot.id : true;
                return condition && this.equalByDay(new Date(event.dt_start));
            });
        }
        return null;
    }
    get currentInstanceData() {
        if (this.currentInstancesData === null || this.forceEmptyInstance) {
            return null;
        }
        return this.currentInstancesData && this.currentInstancesData.length === 1 ? this.currentInstancesData[0] : null;
    }
    get showChooseReservedSlot() {
        return !this.forceShowAddReservedSlot && !this.currentSlot && this.currentInstancesData && this.currentInstancesData.length;
    }
    addSlot(data) {
        if (this.localDate) {
            const value = this.getDateAsSting(this.localDate);
            this.reservedSlots.push({
                // @ts-ignore-next-line
                date: new Date(value),
                ...data,
            });
            this.showSubPopup = false;
        }
    }
    closePopup() {
        this.forceEmptyInstance = false;
        this.selectedSlot = null;
        this.showSubPopup = false;
    }
    closeAddReservedSlotPopup() {
        this.forceShowAddReservedSlot = false;
        this.closePopup();
    }
    setSlot(instance) {
        this.selectedSlot = instance;
        this.currentSlot = this.reservedSlots.find(slot => slot.id === instance.id) || null;
    }
    setShowAddReservedSlot() {
        this.forceEmptyInstance = true;
        this.selectedSlot = null;
        this.forceShowAddReservedSlot = true;
    }
    async createEvent(payload, scope) {
        try {
            const shiftPayload = this.isShift ? {
                ...omit(['origin', 'title'], { ...payload }),
                site_id: this.$route.params.id,
                ...(this.currentInstanceData?.user_id && { user_id: this.currentInstanceData.user_id }),
            } : null;
            this.isRequestSending = true;
            if (this.currentInstanceData && !this.isReservedSotsPopup) {
                if (this.isShift && shiftPayload) {
                    await EventsWebApi.updateShiftEvent(shiftPayload, this.currentInstanceData.id, scope);
                }
                else {
                    await EventsWebApi.updateEvent({ payload, id: this.currentInstanceData.event_id });
                }
            }
            else if (this.forceShowAddReservedSlot || !this.currentInstanceData) {
                if (this.isReservedSotsPopup || this.isUnavailability) {
                    if (this.isUnavailability) {
                        payload.origin = 'unavailability';
                    }
                    else {
                        payload.origin = 'reserved_slot';
                    }
                    await EventsWebApi.createEvent(payload);
                }
                else if (this.isAvailability) {
                    const { dt_start, dt_end, user_id } = payload;
                    const dateStart = formatDateDayMonthYearByLocale(new Date(dt_start), 'en');
                    const dateEnd = formatDateDayMonthYearByLocale(new Date(dt_end), 'en');
                    await EventsWebApi.createAvailabilityEvent({
                        from: dateStart,
                        to: dateEnd,
                        user_id,
                    });
                }
                else if (this.isShift && shiftPayload) {
                    await EventsWebApi.createShiftEvent(shiftPayload);
                }
            }
            this.forceEmptyInstance = false;
            this.lastEventDate = new Date(payload.dt_end);
            this.showSubPopup = false;
            this.getMonthData({ isFullMonthRequest: true });
        }
        catch ({ response }) {
            this.sentNotif(response);
        }
        finally {
            this.isRequestSending = false;
        }
    }
    async removeEvent({ instance, onlyInstance, future }) {
        if (this.isAvailability && instance) {
            this.isRequestSending = true;
            if (onlyInstance) {
                try {
                    await this.deleteAvailabilityInstance(instance.id);
                    this.isRequestSending = false;
                }
                catch (e) {
                    this.isRequestSending = false;
                }
            }
            else {
                try {
                    await this.deleteAvailabilities(instance.user_id);
                    this.isRequestSending = false;
                }
                catch (e) {
                    this.isRequestSending = false;
                }
            }
        }
        else if (this.currentInstanceData) {
            this.isRequestSending = true;
            if (onlyInstance || future || (this.isShift && this.selectedSlot?.recurrence_id)) {
                // eslint-disable-next-line
                const kind = future ? 'future' : (onlyInstance ? null : 'all');
                try {
                    await this.deleteInstance(instance.id, Boolean(future), kind);
                    this.isRequestSending = false;
                }
                catch (e) {
                    this.isRequestSending = false;
                }
            }
            else {
                try {
                    await this.deleteEvent(this.isShift ? instance.id : instance.event_id);
                    this.isRequestSending = false;
                }
                catch {
                    this.isRequestSending = false;
                }
            }
            this.getMonthData({ isFullMonthRequest: true });
        }
    }
    async deleteEvent(id) {
        this.isRequestSending = true;
        try {
            if (this.isShift) {
                await EventsWebApi.deleteFullShiftData(id);
                this.selectedSlot = null;
                this.currentSlot = null;
            }
            else {
                await EventsWebApi.deleteEvent(id);
            }
            // @ts-ignore-next-line
            this.events = this.events.filter((instance) => !this.equalByDay(new Date(instance.dt_start)));
            this.showSubPopup = false;
            this.showReservedSlotPopup = false;
            this.isRequestSending = false;
        }
        catch (e) {
            this.isRequestSending = false;
        }
    }
    async deleteInstance(id, future, kind = null) {
        this.isRequestSending = true;
        try {
            if (this.isShift) {
                await EventsWebApi.deleteFullShiftData(id, kind);
            }
            else {
                await EventsWebApi.deleteCalendarInstance(id, future);
            }
            if (this.isShift) {
                this.selectedSlot = null;
                this.currentSlot = null;
            }
            // @ts-ignore-next-line
            this.events = this.events.filter((instance) => !this.equalByDay(new Date(instance.dt_start)));
            this.showSubPopup = false;
            this.showReservedSlotPopup = false;
            this.isRequestSending = false;
        }
        catch (e) {
            this.isRequestSending = false;
        }
    }
    async deleteAvailabilityInstance(id) {
        this.isRequestSending = true;
        try {
            await EventsWebApi.deleteCalendarAvailabilityInstance(id);
            this.events = this.events.filter((instance) => !this.equalByDay(new Date(instance.due_on)));
            this.availabilitySlots = this.events.map(item => ({ date: new Date(item.due_on), id: item.id }));
            this.showSubPopup = false;
            this.showReservedSlotPopup = false;
            this.isRequestSending = false;
        }
        catch (e) {
            this.isRequestSending = false;
        }
    }
    async deleteAvailabilities(id) {
        this.isRequestSending = true;
        try {
            await EventsWebApi.deleteCalendarAvailabilities(id);
            this.events = [];
            this.availabilitySlots = [];
            this.showSubPopup = false;
            this.showReservedSlotPopup = false;
            this.isRequestSending = false;
        }
        catch (e) {
            this.isRequestSending = false;
        }
    }
    filterDuplicates(slots) {
        // @ts-ignore-next-line
        return slots.filter((element, index) => slots.findIndex(obj => (
        // @ts-ignore-next-line
        obj.event_id === element.event_id &&
            obj.dt_start_view === element.dt_start_view &&
            obj.dt_end_view === element.dt_end_view &&
            obj.dt_start === element.dt_start &&
            obj.dt_end === element.dt_end)) === index);
    }
    prepareSlots(slots) {
        const slotsCopy = clone(slots);
        const pseudoSlots = [];
        for (let i = 0; i < slots.length; i++) {
            const startDate = dayjs(slots[i].dt_start);
            const endDate = dayjs(slots[i].dt_end);
            const diffDays = endDate.startOf('day').diff(startDate.startOf('day'), 'day');
            if (diffDays <= 0)
                continue;
            for (let j = 0; j <= diffDays; j++) {
                const pseudoSlot = clone(slots[i]);
                const currentDay = startDate.add(j, 'day').locale('en');
                // Store original dates for reference
                pseudoSlot.dt_start_view = pseudoSlot.dt_start;
                pseudoSlot.dt_end_view = pseudoSlot.dt_end;
                if (j === 0) {
                    pseudoSlot.dt_start = startDate.locale('en').format(BASE_BACK_TIME_FORMAT);
                    const endTime = diffDays === 0 ? endDate : startDate.hour(23).minute(59).second(0);
                    pseudoSlot.dt_end = endTime.locale('en').format(BASE_BACK_TIME_FORMAT);
                }
                else if (j === diffDays) {
                    pseudoSlot.dt_start = currentDay.hour(0).minute(0).second(0).format(BASE_BACK_TIME_FORMAT);
                    pseudoSlot.dt_end = endDate.locale('en').format(BASE_BACK_TIME_FORMAT);
                }
                else {
                    pseudoSlot.dt_start = currentDay.hour(0).minute(0).second(0).format(BASE_BACK_TIME_FORMAT);
                    pseudoSlot.dt_end = currentDay.hour(23).minute(59).second(0).format(BASE_BACK_TIME_FORMAT);
                }
                pseudoSlot.is_pseudo_slot = true;
                // @ts-ignore-next-line
                pseudoSlots.push(pseudoSlot);
            }
            const currentIndex = slotsCopy.findIndex((item) => item.id === slots[i].id);
            if (currentIndex !== -1) {
                slotsCopy.splice(currentIndex, 1);
            }
        }
        // @ts-ignore-next-line
        return this.filterDuplicates([...slotsCopy, ...pseudoSlots]);
    }
    async getMonthData({ date, isFullMonthRequest }) {
        this.isRequestSending = false;
        const localDate = date || this.localDate;
        let from;
        let to;
        if (!isFullMonthRequest && date) {
            from = date.toDateString();
            to = date.toDateString();
        }
        else {
            const firstDayOfMonth = getFirstDayOfMonth(localDate);
            const lastDayOfMonth = getLastDayOfMonth(localDate, 1);
            from = firstDayOfMonth.toDateString();
            to = lastDayOfMonth.toDateString();
        }
        let origin = 'reserved_slot';
        if (this.isUnavailability) {
            origin = 'unavailability';
        }
        else if (this.isAvailability) {
            origin = 'availability';
        }
        const page = this.meta && this.meta.next_page ? this.meta.next_page : 1;
        // @ts-ignore-next-line
        await this.fetchCalendarDataRec({
            to,
            from,
            user_id: this.$route.params.id,
            // @ts-ignore-next-line
            origin,
            page,
            location: this.startActiveId,
        });
    }
    async fetchCalendarDataByPage({ to, from, user_id, page, location }) {
        try {
            this.isRequestSending = false;
            if (this.isUnavailability) {
                const data = await EventsWebApi.getUnavailabilities({ to, from, user_id, page });
                if (page === 1) {
                    this.events = this.prepareSlots(data.unavailabilities);
                }
                else {
                    // @ts-ignore-next-line
                    this.events = [...this.events, ...this.prepareSlots(data.unavailabilities)];
                }
                this.meta = data.meta;
            }
            else if (this.isAvailability) {
                const data = await EventsWebApi.getAvailabilities({
                    to,
                    from,
                    user_id,
                    page,
                });
                if (page === 1) {
                    // @ts-ignore-next-line
                    this.events = data.flexible_dates;
                }
                else {
                    // @ts-ignore-next-line
                    this.events = [...this.events, ...data.flexible_dates];
                }
                this.meta = data.meta;
            }
            else if (this.isShift) {
                // @ts-ignore-next-line
                const data = await EventsWebApi.getShiftsSlots({
                    from,
                    sp_id: user_id,
                    advisor_id: this.shiftsAdvisor ? this.shiftsAdvisor.id : ``,
                });
                if (page === 1) {
                    this.slots = data.dates.map((date) => ({ date: dayjs(date).toDate() }));
                }
                else {
                    // @ts-ignore-next-line
                    this.slots = [...this.events, ...data.dates].map((date) => ({ date: dayjs(date).toDate() }));
                }
                this.meta = data.meta;
            }
            else {
                // @ts-ignore-next-line
                const data = await EventsWebApi.getReservedSlots({ to, from, user_id, page, location });
                if (page === 1) {
                    this.events = this.prepareSlots(data.reserved_slots);
                }
                else {
                    // @ts-ignore-next-line
                    this.events = [...this.events, ...this.prepareSlots(data.reserved_slots)];
                }
                this.meta = data.meta;
            }
            if (this.isAvailability) {
                this.availabilitySlots = this.events.map(item => ({ date: new Date(item.due_on), id: item.id }));
            }
            else {
                this.reservedSlots = this.events.map(item => ({ date: new Date(item.dt_start), id: item.id }));
            }
        }
        catch (e) {
            this.isRequestSending = false;
        }
    }
    async fetchCalendarDataRec({ to, from, user_id, origin, page, location }) {
        await this.fetchCalendarDataByPage({ to, from, user_id, origin, page, location });
        if (this.meta && this.meta.next_page) {
            this.fetchCalendarDataRec({ to, from, user_id, origin, page: this.meta.next_page, location });
        }
    }
    equalByDay(date) {
        if (!this.localDate) {
            return false;
        }
        return equalByDay(date, this.localDate);
    }
    getDateAsSting(date) {
        return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
    }
    closeReservedSlotPopup() {
        this.showReservedSlotPopup = false;
    }
    openDeletePopup(instance) {
        this.instanceData = instance;
        this.showReservedSlotPopup = true;
    }
    changeCalendarMonth(data, forceDate) {
        if (!data && !forceDate)
            return;
        // @ts-ignore-next-line
        if (forceDate) {
            this.changedCalendarDate = forceDate;
        }
        else if (data) {
            this.changedCalendarDate = data instanceof Date ? data : new Date(data.year, data.month + data.flag + 1, 0);
        }
        if (!this.changedCalendarDate)
            return;
        if (needMakeRequest({ date: this.changedCalendarDate })) {
            this.forceTempHidePopup = true;
            this.getMonthData({ date: this.changedCalendarDate, isFullMonthRequest: true });
        }
    }
    selectDate(date) {
        this.forceTempHidePopup = false;
        if (this.isAvailability && !!this.events.length) {
            // @ts-ignore-next-line
            this.currentAvailabilitySlot = this.events.find((item) => {
                return getDateAsIsoString(new Date(item.due_on)) === getDateAsIsoString(date);
            });
        }
        else {
            this.currentAvailabilitySlot = null;
        }
        this.currentSlot = null;
        this.selectedSlot = null;
        this.showSubPopup = true;
        this.isSkillSelectStep = true;
    }
    async created() {
        if (this.isShift) {
            this.fetchShiftAdvisors();
        }
        await this.getMonthData({ isFullMonthRequest: true });
        this.loading = false;
    }
    currentSlotsHandler(slots) {
        if (slots && slots.length !== 1) {
            this.currentSlot = null;
        }
    }
    localDateHandler(date) {
        this.forceShowAddReservedSlot = false;
    }
    showSubPopupHandler(value) {
        if (value) {
            this.forceTempHidePopup = false;
        }
    }
};
__decorate([
    Watch('currentSlots')
], ReservedSlotsMixin.prototype, "currentSlotsHandler", null);
__decorate([
    Watch('localDate')
], ReservedSlotsMixin.prototype, "localDateHandler", null);
__decorate([
    Watch('showSubPopup')
], ReservedSlotsMixin.prototype, "showSubPopupHandler", null);
ReservedSlotsMixin = __decorate([
    Component
], ReservedSlotsMixin);
export default ReservedSlotsMixin;
;
